import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import {
  TrendingUp,
  Sparkles,
  Loader2,
  AlertCircle,
  CheckCircle2,
  ArrowUp,
  Target,
  FileText,
  Image as ImageIcon,
  Clock,
  Users
} from 'lucide-react';
import { aiAPI } from '../../services/api';

const CampaignOptimizerModal = ({ isOpen, onClose, campaignId }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const categoryIcons = {
    title: FileText,
    story: FileText,
    goal: Target,
    image: ImageIcon,
    urgency: Clock,
    social_proof: Users
  };

  const impactColors = {
    high: 'bg-red-500/20 text-red-400 border-red-500/50',
    medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
    low: 'bg-green-500/20 text-green-400 border-green-500/50'
  };

  const analyzeClick = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await aiAPI.optimizeCampaign(campaignId);
      setResult(data);
    } catch (err) {
      setError('Failed to analyze campaign. Please try again.');
      console.error('Optimizer error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && !result) {
      analyzeClick();
    }
  }, [isOpen, result]);

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreGradient = (score) => {
    if (score >= 80) return 'from-green-500 to-emerald-500';
    if (score >= 60) return 'from-yellow-500 to-orange-500';
    return 'from-red-500 to-orange-500';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-background border-border max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
            AI Campaign Optimizer
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Get AI-powered suggestions to improve your campaign performance
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="relative">
                <Loader2 className="w-12 h-12 text-emerald-400 animate-spin" />
                <Sparkles className="w-6 h-6 text-teal-400 absolute -top-1 -right-1 animate-pulse" />
              </div>
              <p className="mt-4 text-muted-foreground">Analyzing your campaign...</p>
              <p className="text-sm text-muted-foreground">This may take a moment</p>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="w-12 h-12 text-red-400" />
              <p className="mt-4 text-red-400">{error}</p>
              <Button
                onClick={analyzeClick}
                className="mt-4 bg-emerald-500 hover:bg-emerald-600"
              >
                Try Again
              </Button>
            </div>
          )}

          {result && !loading && (
            <div className="space-y-6">
              {/* Overall Score */}
              <div className="p-6 rounded-xl bg-card/50 border border-border">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">Campaign Score</h3>
                    <p className="text-sm text-muted-foreground">Based on AI analysis</p>
                  </div>
                  <div className={`text-4xl font-bold ${getScoreColor(result.overall_score)}`}>
                    {result.overall_score}/100
                  </div>
                </div>
                <Progress 
                  value={result.overall_score} 
                  className="h-3 bg-secondary"
                />
                <div className="mt-4 flex items-center gap-2">
                  <ArrowUp className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 font-medium">Predicted improvement:</span>
                  <span className="text-foreground">{result.predicted_improvement}</span>
                </div>
              </div>

              {/* Suggestions */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-emerald-400" />
                  Optimization Suggestions
                </h3>
                <div className="space-y-3">
                  {result.suggestions
                    .sort((a, b) => a.priority - b.priority)
                    .map((suggestion, index) => {
                      const Icon = categoryIcons[suggestion.category] || FileText;
                      return (
                        <div
                          key={index}
                          className="p-4 rounded-xl bg-card/50 border border-border hover:border-emerald-500/50 transition-colors"
                        >
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-lg bg-secondary">
                              <Icon className="w-5 h-5 text-emerald-400" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-foreground font-medium capitalize">
                                  {suggestion.category.replace('_', ' ')}
                                </span>
                                <Badge className={`${impactColors[suggestion.impact]} border`}>
                                  {suggestion.impact} impact
                                </Badge>
                              </div>
                              <p className="text-muted-foreground text-sm mb-2">
                                <span className="text-red-400">Issue:</span> {suggestion.current_issue}
                              </p>
                              <p className="text-foreground/80 text-sm">
                                <span className="text-green-400">💡 Suggestion:</span> {suggestion.suggestion}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Re-analyze button */}
              <Button
                onClick={analyzeClick}
                variant="outline"
                className="w-full border-border hover:bg-card"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Re-analyze Campaign
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignOptimizerModal;
